/*
 * Offline Banner Component
 * 
 * Real-time network connectivity status banner for the vehicle tracking app
 * 
 * Features:
 * - Shows prominent banner when offline
 * - Auto-hides when back online
 * - Smooth animations and transitions
 * - Mobile-responsive design
 * - Clear messaging about map update status
 * 
 * Purpose:
 * - Informs users when internet connection is lost
 * - Explains that map auto-updates will not work
 * - Provides visual feedback for network status
 */

import React from 'react';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';

/**
 * Offline Banner Component
 * Displays when user loses internet connection
 */
const OfflineBanner = () => {
  const { isOffline, isOnline, wasOffline } = useNetworkStatus();

  // Don't render anything if online and never was offline
  if (isOnline && !wasOffline) {
    return null;
  }

  // Show reconnected banner briefly when coming back online
  if (isOnline && wasOffline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 transform transition-transform duration-500 ease-in-out">
        <div className="bg-green-600 text-white px-4 py-3 shadow-lg">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                  <span className="text-white text-sm">✓</span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center">
                  <p className="font-semibold text-sm sm:text-base">
                    🌐 Connection Restored
                  </p>
                  <p className="text-green-100 text-xs sm:text-sm sm:ml-2">
                    Map updates are now working • Real-time tracking resumed
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show offline banner when no connection
  if (isOffline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 transform transition-transform duration-500 ease-in-out">
        <div className="bg-red-600 text-white px-4 py-3 shadow-lg border-b-2 border-red-700">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                  <span className="text-white text-sm">⚠</span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center">
                  <p className="font-semibold text-sm sm:text-base">
                    🚫 No Internet Connection
                  </p>
                  <p className="text-red-100 text-xs sm:text-sm sm:ml-2">
                    Map will not auto-update • Vehicle tracking offline
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0 hidden sm:block">
                <div className="flex items-center space-x-2 text-red-200 text-xs">
                  <span>🔄 Checking connection...</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Mobile-specific additional info */}
          <div className="mt-2 sm:hidden">
            <div className="flex items-center justify-center space-x-4 text-red-200 text-xs">
              <span>🔄 Checking connection...</span>
              <span>•</span>
              <span>📱 Check your WiFi/Data</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

/**
 * Network Status Indicator Component
 * Small indicator for current network status (optional)
 * @param {string} variant - "default" or "header" (for use on dark backgrounds)
 */
export const NetworkStatusIndicator = ({ className = "", variant = "default" }) => {
  const { isOnline, networkStatus } = useNetworkStatus();

  // Header variant: more visible on blue background
  if (variant === "header") {
    return (
      <div className={`flex items-center space-x-1.5 bg-white/20 px-2.5 py-1 rounded-full backdrop-blur-sm ${className}`}>
        <div
          className={`w-2.5 h-2.5 rounded-full ${
            isOnline ? 'bg-green-400 animate-pulse shadow-sm shadow-green-400/50' : 'bg-red-400 shadow-sm shadow-red-400/50'
          }`}
        />
        <span className={`text-xs font-medium ${
          isOnline ? 'text-green-100' : 'text-red-200'
        }`}>
          {isOnline ? 'Online' : 'Offline'}
        </span>
      </div>
    );
  }

  // Default variant
  return (
    <div className={`flex items-center space-x-1 ${className}`}>
      <div
        className={`w-2 h-2 rounded-full ${
          isOnline ? 'bg-green-500 animate-pulse' : 'bg-red-500'
        }`}
      />
      <span className={`text-xs ${
        isOnline ? 'text-green-600' : 'text-red-600'
      }`}>
        {isOnline ? 'Online' : 'Offline'}
      </span>
    </div>
  );
};

export default OfflineBanner;